/**
 * Studio Staff Permission System
 *
 * Permissions are stored as JSONB on studio_staff rows.
 * Roles (admin/staff) are preset templates that can be customized per-member.
 */

export enum StudioPermission {
  // Bookings
  BOOKINGS_VIEW = 'bookings.view',
  BOOKINGS_APPROVE = 'bookings.approve',
  BOOKINGS_MODIFY = 'bookings.modify',
  BOOKINGS_DELETE = 'bookings.delete',
  BOOKINGS_VIEW_PAYOUT = 'bookings.view_payout',
  BOOKINGS_CANCEL = 'bookings.cancel',
  BOOKINGS_CREATE_MANUAL = 'bookings.create_manual',

  // Analytics
  ANALYTICS_VIEW = 'analytics.view',

  // Payouts
  PAYOUTS_VIEW = 'payouts.view',

  // Discounts
  DISCOUNTS_VIEW = 'discounts.view',
  DISCOUNTS_CREATE = 'discounts.create',
  DISCOUNTS_EDIT = 'discounts.edit',
  DISCOUNTS_DELETE = 'discounts.delete',

  // Rewards
  REWARDS_VIEW = 'rewards.view',
  REWARDS_CREATE = 'rewards.create',
  REWARDS_EDIT = 'rewards.edit',
  REWARDS_DELETE = 'rewards.delete',

  // Classes
  CLASSES_VIEW = 'classes.view',
  CLASSES_MODIFY = 'classes.modify',
  CLASSES_DELETE = 'classes.delete',
  CLASSES_CHECKIN = 'classes.checkin',
  CLASSES_DELETE_REGISTRATIONS = 'classes.delete_registrations',
  CLASSES_VIEW_INSTRUCTOR_PAYOUT = 'classes.view_instructor_payout',
  CLASSES_VIEW_STUDIO_PAYOUT = 'classes.view_studio_payout',

  // Events
  EVENTS_VIEW = 'events.view',
  EVENTS_MODIFY = 'events.modify',
  EVENTS_DELETE = 'events.delete',
  EVENTS_CHECKIN = 'events.checkin',
  EVENTS_CANCEL_REGISTRATIONS = 'events.cancel_registrations',
  EVENTS_MARK_PAID = 'events.mark_paid',
  EVENTS_VIEW_PAYOUT = 'events.view_payout',

  // Class Packs
  CLASS_PACKS_VIEW = 'class_packs.view',
  CLASS_PACKS_CREATE = 'class_packs.create',
  CLASS_PACKS_MODIFY = 'class_packs.modify',
  CLASS_PACKS_DELETE = 'class_packs.delete',
  CLASS_PACKS_VIEW_PURCHASES = 'class_packs.view_purchases',

  // Memberships
  MEMBERSHIPS_VIEW = 'memberships.view',
  MEMBERSHIPS_CREATE = 'memberships.create',
  MEMBERSHIPS_MODIFY = 'memberships.modify',
  MEMBERSHIPS_DELETE = 'memberships.delete',
  MEMBERSHIPS_VIEW_PURCHASES = 'memberships.view_purchases',

  // Instructors
  INSTRUCTORS_VIEW = 'instructors.view',
  INSTRUCTORS_ADD = 'instructors.add',
  INSTRUCTORS_VIEW_PAYOUT_RATE = 'instructors.view_payout_rate',
  INSTRUCTORS_MODIFY_PAYOUT_RATE = 'instructors.modify_payout_rate',

  // Customers
  CUSTOMERS_VIEW = 'customers.view',
  CUSTOMERS_VIEW_PURCHASES = 'customers.view_purchases',

  // Invoices
  INVOICES_VIEW = 'invoices.view',
  INVOICES_CREATE = 'invoices.create',
  INVOICES_EDIT = 'invoices.edit',
  INVOICES_CANCEL = 'invoices.cancel',

  // Settings
  SETTINGS_VIEW = 'settings.view',
  SETTINGS_MODIFY = 'settings.modify',
}

export type StudioPermissionMap = Record<StudioPermission, boolean>;

export type StudioStaffRole = 'admin' | 'staff';

export type StudioStaffStatus = 'pending' | 'active' | 'revoked';

/** Build a permissions map with all keys set to the given value */
function allPermissions(value: boolean): StudioPermissionMap {
  const map = {} as StudioPermissionMap;
  for (const key of Object.values(StudioPermission)) {
    map[key] = value;
  }
  return map;
}

/** Admin default: everything except financial/settings-modify */
export const ADMIN_DEFAULT_PERMISSIONS: StudioPermissionMap = {
  ...allPermissions(true),
  [StudioPermission.PAYOUTS_VIEW]: false,
  [StudioPermission.SETTINGS_MODIFY]: false,
  [StudioPermission.INSTRUCTORS_VIEW_PAYOUT_RATE]: false,
  [StudioPermission.INSTRUCTORS_MODIFY_PAYOUT_RATE]: false,
  [StudioPermission.BOOKINGS_VIEW_PAYOUT]: false,
  [StudioPermission.CLASSES_VIEW_INSTRUCTOR_PAYOUT]: false,
  [StudioPermission.CLASSES_VIEW_STUDIO_PAYOUT]: false,
  [StudioPermission.EVENTS_VIEW_PAYOUT]: false,
};

/** Staff default: view-only basics */
export const STAFF_DEFAULT_PERMISSIONS: StudioPermissionMap = {
  ...allPermissions(false),
  [StudioPermission.BOOKINGS_VIEW]: true,
  [StudioPermission.EVENTS_VIEW]: true,
  [StudioPermission.DISCOUNTS_VIEW]: true,
  [StudioPermission.CUSTOMERS_VIEW]: true,
  [StudioPermission.SETTINGS_VIEW]: true,
};

/** Get the default permissions for a role */
export function getDefaultPermissions(role: StudioStaffRole): StudioPermissionMap {
  return role === 'admin'
    ? { ...ADMIN_DEFAULT_PERMISSIONS }
    : { ...STAFF_DEFAULT_PERMISSIONS };
}

/**
 * Permission categories for UI grouping.
 * Each category maps to sidebar items and permission toggle sections.
 */
export const PERMISSION_CATEGORIES = [
  {
    key: 'bookings',
    label: 'Bookings',
    permissions: [
      StudioPermission.BOOKINGS_VIEW,
      StudioPermission.BOOKINGS_APPROVE,
      StudioPermission.BOOKINGS_MODIFY,
      StudioPermission.BOOKINGS_DELETE,
      StudioPermission.BOOKINGS_VIEW_PAYOUT,
      StudioPermission.BOOKINGS_CANCEL,
      StudioPermission.BOOKINGS_CREATE_MANUAL,
    ],
  },
  {
    key: 'analytics',
    label: 'Analytics',
    permissions: [StudioPermission.ANALYTICS_VIEW],
  },
  {
    key: 'payouts',
    label: 'Payouts',
    permissions: [StudioPermission.PAYOUTS_VIEW],
  },
  {
    key: 'discounts',
    label: 'Discounts',
    permissions: [
      StudioPermission.DISCOUNTS_VIEW,
      StudioPermission.DISCOUNTS_CREATE,
      StudioPermission.DISCOUNTS_EDIT,
      StudioPermission.DISCOUNTS_DELETE,
    ],
  },
  {
    key: 'rewards',
    label: 'Rewards',
    permissions: [
      StudioPermission.REWARDS_VIEW,
      StudioPermission.REWARDS_CREATE,
      StudioPermission.REWARDS_EDIT,
      StudioPermission.REWARDS_DELETE,
    ],
  },
  {
    key: 'classes',
    label: 'Classes',
    permissions: [
      StudioPermission.CLASSES_VIEW,
      StudioPermission.CLASSES_CHECKIN,
      StudioPermission.CLASSES_MODIFY,
      StudioPermission.CLASSES_DELETE,
      StudioPermission.CLASSES_DELETE_REGISTRATIONS,
      StudioPermission.CLASSES_VIEW_INSTRUCTOR_PAYOUT,
      StudioPermission.CLASSES_VIEW_STUDIO_PAYOUT,
    ],
  },
  {
    key: 'events',
    label: 'Events',
    permissions: [
      StudioPermission.EVENTS_VIEW,
      StudioPermission.EVENTS_MODIFY,
      StudioPermission.EVENTS_DELETE,
      StudioPermission.EVENTS_CHECKIN,
      StudioPermission.EVENTS_CANCEL_REGISTRATIONS,
      StudioPermission.EVENTS_MARK_PAID,
      StudioPermission.EVENTS_VIEW_PAYOUT,
    ],
  },
  {
    key: 'class_packs',
    label: 'Class Packs',
    permissions: [
      StudioPermission.CLASS_PACKS_VIEW,
      StudioPermission.CLASS_PACKS_CREATE,
      StudioPermission.CLASS_PACKS_MODIFY,
      StudioPermission.CLASS_PACKS_DELETE,
      StudioPermission.CLASS_PACKS_VIEW_PURCHASES,
    ],
  },
  {
    key: 'memberships',
    label: 'Memberships',
    permissions: [
      StudioPermission.MEMBERSHIPS_VIEW,
      StudioPermission.MEMBERSHIPS_CREATE,
      StudioPermission.MEMBERSHIPS_MODIFY,
      StudioPermission.MEMBERSHIPS_DELETE,
      StudioPermission.MEMBERSHIPS_VIEW_PURCHASES,
    ],
  },
  {
    key: 'instructors',
    label: 'Instructors',
    permissions: [
      StudioPermission.INSTRUCTORS_VIEW,
      StudioPermission.INSTRUCTORS_ADD,
      StudioPermission.INSTRUCTORS_VIEW_PAYOUT_RATE,
      StudioPermission.INSTRUCTORS_MODIFY_PAYOUT_RATE,
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    permissions: [
      StudioPermission.CUSTOMERS_VIEW,
      StudioPermission.CUSTOMERS_VIEW_PURCHASES,
    ],
  },
  {
    key: 'invoices',
    label: 'Invoices',
    permissions: [
      StudioPermission.INVOICES_VIEW,
      StudioPermission.INVOICES_CREATE,
      StudioPermission.INVOICES_EDIT,
      StudioPermission.INVOICES_CANCEL,
    ],
  },
  {
    key: 'settings',
    label: 'Studio Settings',
    permissions: [
      StudioPermission.SETTINGS_VIEW,
      StudioPermission.SETTINGS_MODIFY,
    ],
  },
] as const;

/** Human-readable label for a permission key */
export const PERMISSION_LABELS: Record<StudioPermission, string> = {
  [StudioPermission.BOOKINGS_VIEW]: 'View Bookings',
  [StudioPermission.BOOKINGS_APPROVE]: 'Approve Bookings',
  [StudioPermission.BOOKINGS_MODIFY]: 'Modify Bookings',
  [StudioPermission.BOOKINGS_DELETE]: 'Delete Bookings',
  [StudioPermission.BOOKINGS_VIEW_PAYOUT]: 'View Booking Payout',
  [StudioPermission.BOOKINGS_CANCEL]: 'Cancel Bookings',
  [StudioPermission.BOOKINGS_CREATE_MANUAL]: 'Create Manual Bookings',
  [StudioPermission.ANALYTICS_VIEW]: 'View Analytics',
  [StudioPermission.PAYOUTS_VIEW]: 'View Payouts',
  [StudioPermission.DISCOUNTS_VIEW]: 'View Discounts',
  [StudioPermission.DISCOUNTS_CREATE]: 'Create Discounts',
  [StudioPermission.DISCOUNTS_EDIT]: 'Edit Discounts',
  [StudioPermission.DISCOUNTS_DELETE]: 'Delete Discounts',
  [StudioPermission.REWARDS_VIEW]: 'View Rewards',
  [StudioPermission.REWARDS_CREATE]: 'Create Rewards',
  [StudioPermission.REWARDS_EDIT]: 'Edit Rewards',
  [StudioPermission.REWARDS_DELETE]: 'Delete Rewards',
  [StudioPermission.CLASSES_VIEW]: 'View Classes',
  [StudioPermission.CLASSES_MODIFY]: 'Modify Classes',
  [StudioPermission.CLASSES_DELETE]: 'Delete Classes',
  [StudioPermission.CLASSES_CHECKIN]: 'Check-In Students',
  [StudioPermission.CLASSES_DELETE_REGISTRATIONS]: 'Delete Registrations',
  [StudioPermission.CLASSES_VIEW_INSTRUCTOR_PAYOUT]: 'View Class Instructor Payout',
  [StudioPermission.CLASSES_VIEW_STUDIO_PAYOUT]: 'View Studio Payout',
  [StudioPermission.EVENTS_VIEW]: 'View Events',
  [StudioPermission.EVENTS_MODIFY]: 'Modify Events',
  [StudioPermission.EVENTS_DELETE]: 'Delete Events',
  [StudioPermission.EVENTS_CHECKIN]: 'Check In Attendees',
  [StudioPermission.EVENTS_CANCEL_REGISTRATIONS]: 'Cancel Registrations',
  [StudioPermission.EVENTS_MARK_PAID]: 'Mark Registrations Paid',
  [StudioPermission.EVENTS_VIEW_PAYOUT]: 'View Event Payout',
  [StudioPermission.CLASS_PACKS_VIEW]: 'View Class Packs',
  [StudioPermission.CLASS_PACKS_CREATE]: 'Create Class Packs',
  [StudioPermission.CLASS_PACKS_MODIFY]: 'Modify Class Packs',
  [StudioPermission.CLASS_PACKS_DELETE]: 'Delete Class Packs',
  [StudioPermission.CLASS_PACKS_VIEW_PURCHASES]: 'View Class Pack Purchase History',
  [StudioPermission.MEMBERSHIPS_VIEW]: 'View Memberships',
  [StudioPermission.MEMBERSHIPS_CREATE]: 'Create Memberships',
  [StudioPermission.MEMBERSHIPS_MODIFY]: 'Modify Memberships',
  [StudioPermission.MEMBERSHIPS_DELETE]: 'Delete Memberships',
  [StudioPermission.MEMBERSHIPS_VIEW_PURCHASES]: 'View Membership Purchase History',
  [StudioPermission.INSTRUCTORS_VIEW]: 'View Instructors',
  [StudioPermission.INSTRUCTORS_ADD]: 'Add Instructor',
  [StudioPermission.INSTRUCTORS_VIEW_PAYOUT_RATE]: 'View Instructor Payout Rate',
  [StudioPermission.INSTRUCTORS_MODIFY_PAYOUT_RATE]: 'Modify Instructor Payout Rate',
  [StudioPermission.CUSTOMERS_VIEW]: 'View Customers',
  [StudioPermission.CUSTOMERS_VIEW_PURCHASES]: 'View Customer Purchase History',
  [StudioPermission.INVOICES_VIEW]: 'View Invoices',
  [StudioPermission.INVOICES_CREATE]: 'Create Invoices',
  [StudioPermission.INVOICES_EDIT]: 'Edit Invoices',
  [StudioPermission.INVOICES_CANCEL]: 'Cancel Invoices',
  [StudioPermission.SETTINGS_VIEW]: 'View Studio Settings',
  [StudioPermission.SETTINGS_MODIFY]: 'Modify Studio Settings',
};

/** Interface matching the studio_staff DB row */
export interface StudioStaff {
  id: string;
  studio_id: string;
  profile_id: string | null;
  email: string;
  role: StudioStaffRole;
  permissions: StudioPermissionMap;
  invite_token: string | null;
  invite_expires_at: string | null;
  status: StudioStaffStatus;
  invited_by: string;
  invited_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

/** Staff row joined with profile info (for the management table) */
export interface StudioStaffWithProfile extends StudioStaff {
  profile: {
    id: string;
    full_name: string | null;
    username: string | null;
    profile_photo: string | null;
  } | null;
}

/** What the frontend sends when inviting */
export interface InviteStaffInput {
  email: string;
  role: StudioStaffRole;
  permissions: StudioPermissionMap;
}

/** What the frontend sends when updating permissions */
export interface UpdateStaffInput {
  role?: StudioStaffRole;
  permissions?: StudioPermissionMap;
}

/** Staff role info attached to auth context (lightweight) */
export interface StaffRoleInfo {
  studioId: string;
  studioName: string;
  role: StudioStaffRole;
  permissions: StudioPermissionMap;
}
